/**
 * Shared OAuth postMessage Schema (Story 9.1, Task 1.6)
 *
 * Multi-provider Zod schema for validating postMessage payloads
 * between OAuth callback pages and opener windows.
 *
 * Extracted from google/scopes.ts to support multiple OAuth providers.
 * Uses z.union() because discriminatedUnion requires unique discriminator
 * values, but both providers share "oauth-success" / "oauth-error" types.
 *
 * Security: validate event.origin AND payload schema before processing.
 */

import { z } from "zod";

// ============================================================================
// Provider-specific platform value enums
// ============================================================================

const GOOGLE_OAUTH_PLATFORM_VALUES = [
  "google-analytics-4",
  "google-search-console",
  "google-ads",
  "youtube-analytics",
  "google-business-profile",
] as const;

const META_OAUTH_PLATFORM_VALUES = [
  "facebook-pages",
  "facebook-ads",
  "instagram-business",
  "instagram-ads",
] as const;

const LINKEDIN_OAUTH_PLATFORM_VALUES = [
  "linkedin-pages",
  "linkedin-ads",
] as const;

const TIKTOK_OAUTH_PLATFORM_VALUES = [
  "tiktok-business",
] as const;

const MAILCHIMP_OAUTH_PLATFORM_VALUES = [
  "mailchimp",
] as const;

const SHOPIFY_OAUTH_PLATFORM_VALUES = [
  "shopify",
] as const;

// ============================================================================
// Success schemas per provider
// ============================================================================

/**
 * Story 32-3 (AC #11) — total cataloged-resource count emitted by
 * multi-tenant OAuth callbacks (31-29). COUNT ONLY, never the catalog itself
 * (31-29 AC #6 invariant): `catalogCount > 1` is the web side's signal to
 * open the CatalogPickerDialog after a successful connect. Optional and
 * backward-compatible — payloads without it validate unchanged; payloads
 * with a negative/float value are rejected (schema-gated postMessage).
 */
const CATALOG_COUNT_SCHEMA = z.number().int().nonnegative().optional();

const googleSuccessSchema = z.object({
  type: z.literal("oauth-success"),
  provider: z.literal("google"),
  platforms: z.array(z.enum(GOOGLE_OAUTH_PLATFORM_VALUES)),
  existing: z.array(z.enum(GOOGLE_OAUTH_PLATFORM_VALUES)).optional(),
  catalogCount: CATALOG_COUNT_SCHEMA,
});

const metaSuccessSchema = z.object({
  type: z.literal("oauth-success"),
  provider: z.literal("meta"),
  platforms: z.array(z.enum(META_OAUTH_PLATFORM_VALUES)),
  existing: z.array(z.enum(META_OAUTH_PLATFORM_VALUES)).optional(),
  catalogCount: CATALOG_COUNT_SCHEMA,
});

const linkedInSuccessSchema = z.object({
  type: z.literal("oauth-success"),
  provider: z.literal("linkedin"),
  platforms: z.array(z.enum(LINKEDIN_OAUTH_PLATFORM_VALUES)),
  existing: z.array(z.enum(LINKEDIN_OAUTH_PLATFORM_VALUES)).optional(),
  catalogCount: CATALOG_COUNT_SCHEMA,
});

const tiktokSuccessSchema = z.object({
  type: z.literal("oauth-success"),
  provider: z.literal("tiktok"),
  platforms: z.array(z.enum(TIKTOK_OAUTH_PLATFORM_VALUES)),
  existing: z.array(z.enum(TIKTOK_OAUTH_PLATFORM_VALUES)).optional(),
  catalogCount: CATALOG_COUNT_SCHEMA,
});

const mailchimpSuccessSchema = z.object({
  type: z.literal("oauth-success"),
  provider: z.literal("mailchimp"),
  platforms: z.array(z.enum(MAILCHIMP_OAUTH_PLATFORM_VALUES)).min(1),
  existing: z.array(z.enum(MAILCHIMP_OAUTH_PLATFORM_VALUES)).optional(),
  catalogCount: CATALOG_COUNT_SCHEMA,
});

const shopifySuccessSchema = z.object({
  type: z.literal("oauth-success"),
  provider: z.literal("shopify"),
  platforms: z.array(z.enum(SHOPIFY_OAUTH_PLATFORM_VALUES)).min(1),
  existing: z.array(z.enum(SHOPIFY_OAUTH_PLATFORM_VALUES)).optional(),
  catalogCount: CATALOG_COUNT_SCHEMA,
});

// ============================================================================
// Error schemas per provider
// ============================================================================

const googleErrorSchema = z.object({
  type: z.literal("oauth-error"),
  provider: z.literal("google"),
  error: z.string().max(500),
  errorCategory: z.string().max(100).optional(),
});

const metaErrorSchema = z.object({
  type: z.literal("oauth-error"),
  provider: z.literal("meta"),
  error: z.string().max(500),
  errorCategory: z.string().max(100).optional(),
});

const linkedInErrorSchema = z.object({
  type: z.literal("oauth-error"),
  provider: z.literal("linkedin"),
  error: z.string().max(500),
  errorCategory: z.string().max(100).optional(),
});

const tiktokErrorSchema = z.object({
  type: z.literal("oauth-error"),
  provider: z.literal("tiktok"),
  error: z.string().max(500),
  errorCategory: z.string().max(100).optional(),
});

const mailchimpErrorSchema = z.object({
  type: z.literal("oauth-error"),
  provider: z.literal("mailchimp"),
  error: z.string().max(500),
  errorCategory: z.string().max(100).optional(),
});

const shopifyErrorSchema = z.object({
  type: z.literal("oauth-error"),
  provider: z.literal("shopify"),
  error: z.string().max(500),
  errorCategory: z.string().max(100).optional(),
});

// ============================================================================
// Combined multi-provider schema
// ============================================================================

/**
 * Zod schema for validating postMessage payloads between
 * OAuth callback pages and opener windows.
 *
 * Supports Google, Meta, LinkedIn, and TikTok providers via z.union().
 * Each variant includes "type" (success/error) and "provider" (google/meta/linkedin/tiktok)
 * for routing. Uses z.union() instead of z.discriminatedUnion() because
 * Zod v4 requires unique discriminator values per variant.
 */
export const INTEGRATION_OAUTH_POST_MESSAGE_SCHEMA = z.union([
  googleSuccessSchema,
  metaSuccessSchema,
  linkedInSuccessSchema,
  tiktokSuccessSchema,
  mailchimpSuccessSchema,
  shopifySuccessSchema,
  googleErrorSchema,
  metaErrorSchema,
  linkedInErrorSchema,
  tiktokErrorSchema,
  mailchimpErrorSchema,
  shopifyErrorSchema,
]);

export type IntegrationOAuthPostMessage = z.infer<
  typeof INTEGRATION_OAUTH_POST_MESSAGE_SCHEMA
>;
